import { useMemo } from "react";
import { useProfileStore } from "@/store/profileStore";
import { useSettingsStore } from "@/store/settingsStore";
import { usePermissions } from "@/context/PermissionsProvider";
import type { LegalEntityHierarchy } from "@/services/profileService";

export interface ScopeBranchOption {
  id: string;
  name: string;
  legalEntityId: string;
  legalEntityName: string;
  /** Branch logo, used as the reach-out header image fallback. */
  logoUrl?: string | null;
}

export interface ScopeEntityOption {
  id: string;
  name: string;
  /** Indent level in the hierarchy, so a sub-entity reads as one. */
  depth: number;
  branches: ScopeBranchOption[];
}

export interface ScopeOptions {
  entities: ScopeEntityOption[];
  branches: ScopeBranchOption[];
  /** id → display name for every pickable entity and branch. */
  labels: Record<string, string>;
  /**
   * True when the signed-in user holds exactly one branch: there is nothing to choose,
   * so the picker renders their scope read-only rather than as a dropdown.
   */
  locked: boolean;
  /** What to show while locked. */
  lockedLabel: string | null;
  loading: boolean;
}

function flatten(
  entities: LegalEntityHierarchy[],
  depth = 0,
): (LegalEntityHierarchy & { depth: number })[] {
  return entities.reduce<(LegalEntityHierarchy & { depth: number })[]>((acc, le) => {
    acc.push({ ...le, depth });
    if (le.subEntities?.length) acc.push(...flatten(le.subEntities, depth + 1));
    return acc;
  }, []);
}

/**
 * Everything the scope picker can offer, derived from `/profile` and `/me`.
 *
 * The tree that `/profile` returns is already pruned to what the user may reach, so the
 * picker never has to filter for permissions itself — it offers what it is given, and the
 * server re-checks scope on every request regardless.
 */
export function useScopeOptions(): ScopeOptions {
  const { profileData, isLoading } = useProfileStore();
  const { me } = usePermissions();
  const legalEntityId = useSettingsStore((s) => s.legalEntityId);

  return useMemo(() => {
    const allRoots = profileData?.legalEntities ?? [];
    // One email can belong to several unrelated organisations, and `/profile` returns a
    // root per organisation. Offering all of them would let a master user filter a list by
    // an entity from an account they are not currently in, so the picker is confined to the
    // organisation selected in the topbar: the root whose subtree holds the current id.
    const selectedOrg = legalEntityId
      ? allRoots.find((root) => flatten([root]).some((le) => le.id === legalEntityId))
      : undefined;
    const roots = selectedOrg ? [selectedOrg] : allRoots;
    const flat = flatten(roots);

    const entities: ScopeEntityOption[] = flat.map((le) => ({
      id: le.id,
      name: le.name,
      depth: le.depth,
      branches: (le.branches ?? []).map((br) => ({
        id: br.id,
        name: br.name ?? "Unnamed branch",
        legalEntityId: le.id,
        legalEntityName: le.name,
        logoUrl: br.logoUrl,
      })),
    }));

    const branches = entities.flatMap((e) => e.branches);

    const labels: Record<string, string> = {};
    for (const e of entities) labels[e.id] = e.name;
    for (const b of branches) labels[b.id] = b.name;

    // Mirrors the rule the topbar used: one allowed branch means the user is pinned to it.
    // Master users are exempt — their branch list is the whole organisation's.
    const isMasterUser = profileData?.userType === "ATLAS_MASTER_USER";
    const allowedBranchIds = me?.allowedBranchIds ?? [];
    const lockedBranchId =
      allowedBranchIds.length === 1 ? allowedBranchIds[0] : (me?.branchId ?? null);
    const locked = Boolean(lockedBranchId) && !isMasterUser && branches.length <= 1;
    const lockedBranch = branches.find((b) => b.id === lockedBranchId);

    return {
      entities,
      branches,
      labels,
      locked,
      lockedLabel: locked
        ? [lockedBranch?.legalEntityName, lockedBranch?.name].filter(Boolean).join(" · ") || null
        : null,
      loading: isLoading,
    };
  }, [profileData, isLoading, me, legalEntityId]);
}
